/**
 * Phone Country Rules
 *
 * Country verdict for a phone number, by the number's own dial code. The same
 * algorithm as Phone_Rules::verdict() in PHP, over the same generated table
 * (accountData.validationRules.phone.country_rules), so the browser and the
 * server agree by construction. Change one, change both.
 *
 * No DOM manipulation — this module is purely functional.
 *
 * @package betbeast
 */

const NONE = { status: 'none', class: null, cc: null, region: null, mode: 'off', block: false };

// Stripped from a copy of the number: the same explicit list as Phone_Rules::STRIP.
const STRIP = /[\t\n\v\f\r \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff().\-]/g;

/**
 * Country verdict for a phone number.
 *
 * A rejected number on a shared dial code is judged for the preferred region
 * when it shares the code, else for the code's main region (see PHP).
 *
 * @param {string} phone        Number as entered, e.g. "+1 (416) 555-1234".
 * @param {string} surface      'signup', 'profile' or 'account'.
 * @param {Object} countryRules validationRules.phone.country_rules.
 * @param {string} [preferred]  ISO code: the flag picked, or the account country.
 * @returns {{status: string, class: (string|null), cc: (string|null), region: (string|null), mode: string, block: boolean}}
 */
export function phoneCountryVerdict(phone, surface, countryRules, preferred) {
    try {
        if (!hasRules(countryRules)) {
            return NONE;
        }

        const copy  = String(phone || '').replace(STRIP, '');
        const match = /^\+(\d+)$/.exec(copy);
        if (!match) {
            return NONE;
        }
        const digits = match[1];

        let cc = null;
        for (let size = 1; size <= 3 && cc === null; size++) {
            const prefix = digits.substring(0, size);
            if (Object.prototype.hasOwnProperty.call(countryRules.by_cc, prefix)) {
                cc = prefix;
            }
        }

        if (cc === null) {
            return finish(countryRules, 'unknown_code', null, null, countryRules.mode, surface);
        }

        const national = digits.substring(cc.length);
        const regions  = countryRules.by_cc[cc];
        const rules    = countryRules.rules;
        const wanted   = String(preferred || '').toUpperCase();
        const home     = regions.indexOf(wanted) !== -1 ? wanted : regions[0];

        if (national.length > countryRules.input_max_digits) {
            return finish(countryRules, 'structural', cc, home, rules[home].mode, surface);
        }

        for (const iso of regions) {
            if (accepts(rules[iso], national)) {
                return finish(countryRules, null, cc, iso, rules[iso].mode, surface);
            }
        }

        return finish(countryRules, classify(rules, regions, home, cc, national), cc, home, rules[home].mode, surface);
    } catch (e) {
        return NONE;
    }
}

/**
 * Message for a flagged verdict.
 *
 * @param {Object}  verdict       Result of phoneCountryVerdict().
 * @param {Object}  countryRules  validationRules.phone.country_rules.
 * @param {Object}  [countryData] window.COUNTRY_PHONE_DATA, for country names.
 * @param {boolean} [saved]       The number is the one already on the account.
 * @returns {string}
 */
export function phoneCountryMessage(verdict, countryRules, countryData, saved) {
    const messages = countryRules.messages || {};
    const rule     = verdict.region ? countryRules.rules[verdict.region] : null;
    const name     = verdict.region && countryData && countryData[verdict.region] ? countryData[verdict.region].name : '';
    const key      = saved && !verdict.block ? 'saved' : (verdict.block ? 'blocked' : 'flagged');

    if (!name || !rule || !rule.example) {
        return messages[key + '_no_country'] || '';
    }

    return (messages[key] || '')
        .replace('{country}', name)
        .replace('{example}', rule.example);
}

/**
 * Example number for a dial code, for the hint above the field.
 *
 * @param {string} dialCode     Dial code with or without "+", e.g. "44".
 * @param {Object} countryRules validationRules.phone.country_rules.
 * @param {string} [region]     Region to prefer, e.g. the selected country.
 * @returns {string} E.164 example, or '' when the rules for it are off or unknown.
 */
export function phoneCountryExample(dialCode, countryRules, region) {
    if (!hasRules(countryRules)) {
        return '';
    }
    const cc = dialCodeOf(dialCode, countryRules);
    if (cc === null) {
        return '';
    }
    const regions = countryRules.by_cc[cc];
    const iso     = region && regions.indexOf(region) !== -1 ? region : regions[0];
    const rule    = countryRules.rules[iso];
    return rule && rule.mode !== 'off' ? rule.example : '';
}

/**
 * Hint for the field: the example number of a dial code, laid out with the
 * region's template, e.g. "e.g. +44 7400 123456".
 *
 * With `national`, the dial code is left out ("e.g. 7400 123456"): the modals
 * take the code from the list beside the field and the number without it. The
 * code left out is the list's whole value, so a NANP territory ("1876") shows
 * the seven digits that remain to type.
 *
 * @param {string}  dialCode     Dial code with or without "+".
 * @param {Object}  countryRules validationRules.phone.country_rules.
 * @param {string}  [region]     Region to prefer.
 * @param {boolean} [national]   Leave the dial code out.
 * @returns {string} '' when there is nothing to show.
 */
export function phoneCountryHint(dialCode, countryRules, region, national) {
    const example = phoneCountryExample(dialCode, countryRules, region);
    if (!example) {
        return '';
    }

    const cc      = dialCodeOf(dialCode, countryRules);
    const regions = countryRules.by_cc[cc];
    const iso     = region && regions.indexOf(region) !== -1 ? region : regions[0];
    const layout  = countryRules.rules[iso].template;

    let shown = example;
    if (layout) {
        const digits = example.replace(/\D/g, '').substring(cc.length);
        let i = 0;
        shown = layout.replace(/#/g, function () { return digits.charAt(i++); });
    }

    if (national) {
        shown = withoutDialCode(shown, String(dialCode || '').replace(/\D/g, ''));
    }

    return (countryRules.messages && countryRules.messages.hint ? countryRules.messages.hint : '{example}').replace('{example}', shown);
}

// A laid-out number without its first `code.length` digits and the separators
// that followed them: "+1 876-512-3456" less "1876" is "512-3456".
function withoutDialCode(shown, code) {
    let seen = 0;
    let i = 0;
    while (i < shown.length && seen < code.length) {
        if (/\d/.test(shown.charAt(i))) {
            seen++;
        }
        i++;
    }
    return shown.substring(i).replace(/^[\s\-.()]+/, '');
}

// The dial code within what the select holds: NANP territories carry "1876",
// "1684" and the like, while the table knows only "1". Same 1-3 digit rule as
// the verdict.
function dialCodeOf(value, countryRules) {
    const digits = String(value || '').replace(/\D/g, '');
    for (let size = 1; size <= 3; size++) {
        const prefix = digits.substring(0, size);
        if (prefix && Object.prototype.hasOwnProperty.call(countryRules.by_cc, prefix)) {
            return prefix;
        }
    }
    return null;
}

// An empty table must mean "no verdict", never "every code is unknown".
function hasRules(countryRules) {
    return !!countryRules
        && !!countryRules.rules
        && !!countryRules.by_cc
        && Object.keys(countryRules.by_cc).length > 0;
}

function toRegex(pattern) {
    return new RegExp('^(?:' + pattern + ')$');
}

function accepts(rule, national) {
    if (rule.pattern !== '') {
        return toRegex(rule.pattern).test(national);
    }
    return rule.mobile !== ''
        && rule.lengths.indexOf(national.length) !== -1
        && toRegex(rule.mobile).test(national);
}

function classify(rules, regions, home, cc, national) {
    // An exception pattern is the structural part of the rule (decision 37).
    if (rules[home].pattern !== '') {
        return 'structural';
    }

    // NANP: area code and exchange never start with 0 or 1.
    if (cc === '1' && !/^[2-9]\d{2}[2-9]/.test(national)) {
        return 'structural';
    }

    for (const iso of regions) {
        if (rules[iso].possible.indexOf(national.length) !== -1) {
            return 'prefix_unknown';
        }
    }

    return 'structural';
}

function finish(countryRules, cls, cc, region, mode, surface) {
    if (mode === 'off') {
        return { status: 'none', class: null, cc: cc, region: region, mode: mode, block: false };
    }

    return {
        status: cls === null ? 'ok' : 'flagged',
        class:  cls,
        cc:     cc,
        region: region,
        mode:   mode,
        block:  cls !== null
            && mode === 'strict'
            && countryRules.blocking_classes.indexOf(cls) !== -1
            && countryRules.never_blocks_on.indexOf(surface) === -1
    };
}
